/** Send an email template to every lead in a lead list — variables auto-filled per lead. */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Send, Loader2, Users, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const SendToListModal = ({ template, onClose }) => {
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState("");
  const [leads, setLeads] = useState([]);
  const [fromName, setFromName] = useState("Projexino Solutions");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/outreach/lead-lists").then(({ data }) => setLists(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!listId) { setLeads([]); return; }
    api.get("/outreach/leads", { params: { list_id: listId } })
      .then(({ data }) => setLeads(data.items || [])).catch(() => {});
  }, [listId]);

  const real = leads.filter((l) => l.email && !l.email.includes("@google-maps.lead") && !l.email.startsWith("noemail+"));
  const synthetic = leads.length - real.length;

  const send = async () => {
    if (!listId) return toast.error("Pick a lead list");
    if (!real.length) return toast.error("This list has no real email addresses — enrich the leads first");
    if (!window.confirm(`Send "${template.name}" to ${real.length} lead(s)? Names, companies and emails are filled automatically from the list.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/email/templates/send-to-list", {
        template_id: template.id, list_id: listId, from_name: fromName,
      });
      setResult(data);
      if (data.sent > 0) toast.success(`Sent ${data.sent}/${data.total} emails`);
      else if (!data.gmail_connected) toast.error("Gmail is not connected — reconnect it in Settings, then retry");
      else toast.error("Nothing sent — see the per-lead results");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <motion.div onClick={(e) => e.stopPropagation()} initial={{ scale: 0.97 }} animate={{ scale: 1 }}
        data-testid="send-to-list-modal" className="my-8 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-[#0F2042]">Mass send · {template.name}</h3>
            <p className="text-xs text-slate-500">Pick a lead list — names, emails &amp; companies are taken from the list automatically. No manual values needed.</p>
          </div>
          <button onClick={onClose} data-testid="send-to-list-close"><X size={18} /></button>
        </div>

        {!result ? (
          <div className="space-y-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Lead list</div>
              <select value={listId} onChange={(e) => setListId(e.target.value)} data-testid="send-list-select"
                className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm">
                <option value="">— Choose a lead list —</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({(l.lead_ids || []).length} leads)</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">From name</div>
              <input value={fromName} onChange={(e) => setFromName(e.target.value)} data-testid="send-list-fromname"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
            </div>
            {listId && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="send-list-preview">
                <div className="flex items-center gap-2 text-xs font-bold text-[#0F2042]">
                  <Users size={13} className="text-[#F97316]" /> {real.length} will receive it
                  {synthetic > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">{synthetic} skipped (needs enrichment)</span>}
                </div>
                <div className="mt-2 max-h-36 space-y-1 overflow-auto text-[11px] text-slate-600">
                  {real.slice(0, 12).map((l) => (
                    <div key={l.id} className="flex justify-between gap-2">
                      <span className="truncate font-semibold">{[l.first_name, l.last_name].filter(Boolean).join(" ") || l.email.split("@")[0]}</span>
                      <span className="truncate text-slate-400">{l.company || ""}</span>
                      <span className="truncate">{l.email}</span>
                    </div>
                  ))}
                  {real.length > 12 && <div className="text-slate-400">…and {real.length - 12} more</div>}
                </div>
              </div>
            )}
            <button onClick={send} disabled={busy || !listId} data-testid="send-list-go"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0F2042] py-3 text-sm font-bold text-white disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {busy ? "Sending…" : `Send to ${real.length || "…"} lead(s)`}
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="send-list-results">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-emerald-50 p-2 ring-1 ring-emerald-200"><div className="text-lg font-bold text-emerald-700">{result.sent}</div><div className="text-[10px] font-bold uppercase text-emerald-600">Sent</div></div>
              <div className="rounded-xl bg-rose-50 p-2 ring-1 ring-rose-200"><div className="text-lg font-bold text-rose-700">{result.failed}</div><div className="text-[10px] font-bold uppercase text-rose-600">Failed</div></div>
              <div className="rounded-xl bg-amber-50 p-2 ring-1 ring-amber-200"><div className="text-lg font-bold text-amber-700">{result.skipped}</div><div className="text-[10px] font-bold uppercase text-amber-600">Skipped</div></div>
            </div>
            {!result.gmail_connected && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-bold text-rose-700">
                Gmail is not connected — reconnect it in Settings → Gmail, then send again.
              </div>
            )}
            <div className="max-h-56 overflow-auto rounded-xl border border-slate-200">
              {(result.results || []).map((r, i) => (
                <div key={i} className="flex items-center gap-2 border-b border-slate-100 px-3 py-1.5 text-[11px]">
                  {r.status === "sent" ? <CheckCircle2 size={12} className="shrink-0 text-emerald-500" />
                    : r.status === "skipped" ? <MinusCircle size={12} className="shrink-0 text-amber-500" />
                    : <XCircle size={12} className="shrink-0 text-rose-500" />}
                  <span className="w-52 truncate font-semibold">{r.email}</span>
                  <span className="truncate text-slate-500">{r.reason || r.subject || ""}</span>
                </div>
              ))}
            </div>
            <button onClick={onClose} className="w-full rounded-full border border-slate-300 py-2.5 text-sm font-bold text-slate-600">Done</button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};
